import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getReturnPath } from '../utils/returnPath';
import './ErrorPage.css';

const pages = {
  404: {
    label: 'SCENE NOT FOUND',
    title: 'This scene is missing.',
    description: 'The page you’re looking for may have moved, or the link may be out of date. There’s still plenty to watch.',
    note: 'Your next great story is waiting.',
    icon: 'M8 3h8l4 4v14H4V3h4m0 0v6h8V3M8 14h8m-8 4h5',
  },
  401: {
    label: 'SIGN IN REQUIRED',
    title: 'Your seat is waiting.',
    description: 'Sign in to continue to this page. If your session has expired, signing in again will get you back on track.',
    note: 'One account. All your favourite stories.',
    icon: 'M6 11V8a6 6 0 0 1 12 0v3M4 11h16v11H4V11m8 4v3',
  },
  403: {
    label: 'ACCESS RESTRICTED',
    title: 'This area is off-screen.',
    description: 'Your account doesn’t have permission to open this page. You can head back and explore the movies and series available to you.',
    note: 'The rest of the cinema is still open.',
    icon: 'M12 2 3 6v6c0 5 9 10 9 10s9-5 9-10V6l-9-4m-4 10h8',
  },
};

export default function ErrorPage({ status = 404 }) {
  const code = pages[status] ? status : 404;
  const page = pages[code];
  const location = useLocation();
  const heading = useRef(null);
  const from = getReturnPath(location.state?.from);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${code} · ${page.label.toLowerCase()} | Zimetafsiriwa`;
    heading.current?.focus({ preventScroll: true });
    return () => { document.title = previousTitle; };
  }, [code, page]);

  return (
    <section className={`error-page error-page--${code}`} aria-labelledby="error-page-title">
      <div className="error-page__content">
        <span className="error-page__eyebrow"><span aria-hidden="true" />{page.label}</span>
        <p className="error-page__status">ERROR {code}</p>
        <h1 id="error-page-title" ref={heading} tabIndex={-1}>{page.title}</h1>
        <p className="error-page__description">{page.description}</p>
        <div className="error-page__actions">
          {code === 401 ? (
            <>
              <Link className="error-page__button error-page__button--primary" to="/login" state={{ from }}>Sign in to continue <span aria-hidden="true">→</span></Link>
              <Link className="error-page__button error-page__button--secondary" to="/">Back to home</Link>
            </>
          ) : (
            <>
              <Link className="error-page__button error-page__button--primary" to="/">Back to home <span aria-hidden="true">→</span></Link>
              <Link className="error-page__button error-page__button--secondary" to="/?filter=series">Explore series</Link>
            </>
          )}
        </div>
        <p className="error-page__note">{page.note}</p>
      </div>
      <div className="error-page__art" aria-hidden="true">
        <div className="error-page__frame">
          <div className="error-page__frame-top"><span>ZIME CINEMA</span><span>●</span></div>
          <span className="error-page__code">{code}</span>
          <div className="error-page__icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d={page.icon} /></svg></div>
          <div className="error-page__frame-bottom"><span>END OF THIS SCENE</span><span>↗</span></div>
        </div>
      </div>
    </section>
  );
}
